import React, {useEffect, useMemo, useState} from "react";
import {B2Site} from "../../../data/client.gen";
import {notBlank} from "../../../utils";
import {AppSimplifiedShell} from "../../../component/app-simplified-shell";
import {useOrder} from "../../../context/order";
import {BoxPackagePrice} from "../../../data/sites";
import {Alert} from "@mui/material";
import {useLocalization} from "../../../i18n";
import {OrderOverview} from "./order-overview";
import {OrderForm} from "./order-form";
import {getZoneDateTimeNow, parseZoneDateTimeFromDate} from "../../../dates-utils";
import {ZonedDateTime} from "@variocube/app-ui";

type FlowState = 'Overview' | 'Form';

export function OrderFlowSimplified({site}: { site: B2Site }) {
	const {t} = useLocalization();
	const {setData} = useOrder();
	const {
		activeUntil,
		boxes: siteBoxes,
		bookingUnit,
		zoneId = 'UTC'
	} = site;

	const [flowState, setFlowState] = useState<FlowState>('Overview');

	const now = getZoneDateTimeNow(zoneId);
	const {
		isSiteExpired,
		box,
		baseTime,
	} = useMemo(() => {
		const isSiteExpired = notBlank(activeUntil)
			&& ZonedDateTime.compare(parseZoneDateTimeFromDate(activeUntil as string, zoneId), now) < 1;
		const box = siteBoxes.length > 0
			? siteBoxes[0]
			: undefined;
		const baseTime = computeBaseTime(site);
		return {
			isSiteExpired,
			box,
			baseTime
		}
	}, [site]);

	// Prepare the default cart value with date time based on the site configuration
	useEffect(() => {
		let from;
		let until;
		let fromDateTime;
		let hours;
		let boxes: Record<string, number> = {};

		// assuming the site only has 1 box type,
		// get the first box from the map
		const box = siteBoxes.length > 0 ? siteBoxes[0] : undefined;
		if (box !== undefined && box.packagePrice !== undefined) {
			const packagePrice = box.packagePrice as BoxPackagePrice;

			// get the first unit from package price from box or default to 1
			let computedUnit = 1;
			if (Object.keys(packagePrice).length > 0) {
				const [unit] = Object.entries(packagePrice)[0];
				computedUnit = parseInt(unit);
			}

			if (bookingUnit === 'Days') {
				from = baseTime.toJSON();
				until = baseTime.add({ days: computedUnit - 1 }).toJSON()
			}
			if (bookingUnit === 'Hours') {
				fromDateTime = now.toInstant().toJSON();
				hours = computedUnit;
			}
			boxes[box.type] = computedUnit;
		}

		const orderData = {
			dates: {
				from,
				until,
				fromDateTime,
				hours
			},
			boxes: {
				types: boxes
			},
			discount: {},
			customer: {}
		};
		setData(orderData);
		console.info('Order prepared', orderData);
	}, [siteBoxes, bookingUnit, zoneId, baseTime]);

	return (
		<AppSimplifiedShell>
			{isSiteExpired && (
				<Alert severity="warning">{t('sites.eventExpired')}</Alert>
			)}
			{!isSiteExpired && (
				<>
					{flowState === 'Overview' && (
						<OrderOverview
							site={site}
							baseTime={baseTime}
							box={box}
							onNext={() => setFlowState('Form')}
						/>
					)}
					{flowState === 'Form' && (
						<OrderForm
							site={site}
							box={box}
						/>
					)}
				</>
			)}
		</AppSimplifiedShell>
	)
}

function computeBaseTime(site: B2Site) {
	let baseTime = getZoneDateTimeNow(site.zoneId ?? 'UTC');
	console.info('Start computing base time, current', baseTime.toJSON())

	// if site has configured accessible timeframe,
	// evaluate from the site accessibility
	if (!site.alwaysAccessible
		&& site.accessibleFrom
		&& site.accessibleUntil
	) {
		// site has +1 day of accessibility and the current time is still within the accessibility timeframe
		if (site.accessibleFrom > site.accessibleUntil) {
			console.group('Non-linear site accessibility timeframe');
			console.info('Site has +1 day of accessibility configured.')
			const [fromHour, fromMinute] = site.accessibleFrom.split(":").map(d => parseInt(d));
			const [untilHour, untilMinute] = site.accessibleUntil.split(":").map(d => parseInt(d));

			let effectiveFrom = baseTime.with({ hour: fromHour, minute: fromMinute });
			let effectiveUntil = baseTime.add({ days: 1 }).with({ hour: untilHour, minute: untilMinute });
			let midnight = effectiveUntil.with({ hour: 0, minute: 0, second: 0, millisecond: 0 });
			console.info('Initial effective from', effectiveFrom.toJSON());
			console.info('Initial effective until', effectiveUntil.toJSON());
			console.info('Initial midnight', midnight.toJSON());

			// if current hour is before accessible until, set back 1 day
			if (
				ZonedDateTime.compare(baseTime, effectiveUntil) < 1
				&& baseTime.toPlainTime().toJSON() < site.accessibleUntil
			) {
				console.info('Current hour is before accessible until, adjusting timeframe back by 1 day.');
				effectiveFrom = effectiveFrom.subtract({ days: 1 });
				effectiveUntil = effectiveUntil.subtract({ days: 1 });
				midnight = midnight.subtract({ days: 1 });
				console.info('adjusted effective from', effectiveFrom.toJSON());
				console.info('adjusted effective until', effectiveUntil.toJSON());
				console.info('adjusted midnight', midnight.toJSON());
			}

			console.info('==> effectiveFrom', effectiveFrom.toJSON());
			console.info('<== effectiveUntil', effectiveUntil.toJSON());
			console.info('OOO midnight', midnight.toJSON());

			// base time is before effective from => keep current day
			if (ZonedDateTime.compare(baseTime, effectiveFrom) < 1) {
				console.info('Booking time is before effective from, booking will be made on the same day');
			}
			// base time is before midnight (based from effective until) => keep current day
			else if (ZonedDateTime.compare(baseTime, midnight) < 1) {
				console.info('Booking time is before midnight (from affective until), booking will be made on the same day');
			}
			// base time is after midnight (based from effective until) and before effective until => minus 1 day
			else if (ZonedDateTime.compare(baseTime, midnight) > -1
				&& ZonedDateTime.compare(baseTime, effectiveUntil) < 1
			) {
				console.info('Booking time is after midnight (from effective until), booking will be made with 1 day before');
				baseTime = baseTime.subtract({ days: 1 });
			}
			console.groupEnd();
		}

		// now is after accessible until => + 1 days
		else if (baseTime.toPlainTime().toJSON() > site.accessibleFrom
			&& baseTime.toPlainTime().toJSON() > site.accessibleUntil
		) {
			console.info('Booking time started after event, plus 1 day')
			baseTime = baseTime.add({ days: 1 });
		}
	}

	return baseTime;
}
